import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Upload, Trash2, Eye, Image as ImageIcon, RefreshCw, X, Check } from 'lucide-react';
import { imageAssetsService, ImageAsset, ImageCategory } from '../../services/imageAssetsService';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { cn } from '../../lib/utils';

export function ImagesTab() {
  const [categories, setCategories] = useState<ImageCategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('hardware');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadCategory, setUploadCategory] = useState<string>('custom');
  const [uploadSubcategory, setUploadSubcategory] = useState<string>('uploads');
  const [dragActive, setDragActive] = useState(false);
  const [selectedAssets, setSelectedAssets] = useState<Set<string>>(new Set());
  const [previewAsset, setPreviewAsset] = useState<ImageAsset | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadCategories = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await imageAssetsService.getAvailableAssets();
      setCategories(response.categories);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load images');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const currentCategory = categories.find(c => c.name === selectedCategory);

  const allAssets: (ImageAsset & { subName: string })[] = currentCategory
    ? currentCategory.subcategories.flatMap(sub =>
        sub.assets.map(a => ({ ...a, subName: sub.displayName }))
      )
    : [];

  const isSyncedAsset = (asset: ImageAsset): boolean => {
    return asset.category !== 'custom' && !asset.subcategory?.startsWith('custom');
  };

  const handleUpload = async (file: File) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { setError('Only image files are allowed'); return; }
    if (file.size > 10 * 1024 * 1024) { setError('Image must be less than 10MB'); return; }

    setIsUploading(true);
    setError(null);
    try {
      const result = await imageAssetsService.uploadCustomImage(file, {
        category: uploadCategory,
        subcategory: uploadSubcategory,
      });
      if (result.success) {
        setSuccessMessage(`Image "${file.name}" uploaded successfully!`);
        await loadCategories();
      } else {
        setError(result.error || 'Upload failed');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleFileInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) handleUpload(file);
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleUpload(e.dataTransfer.files[0]);
    }
  };

  const handleDelete = async (asset: ImageAsset) => {
    if (isSyncedAsset(asset)) {
      setError('Cannot delete synced assets. Only custom uploads can be deleted.');
      return;
    }
    if (!window.confirm(`Are you sure you want to delete "${asset.displayName}"?`)) return;

    setError(null);
    try {
      const path = `${asset.subcategory}/${asset.filename}`;
      const result = await imageAssetsService.deleteCustomImage(path);
      if (result.success) {
        setSuccessMessage(`Image "${asset.displayName}" deleted successfully!`);
        setSelectedAssets(prev => { const next = new Set(prev); next.delete(asset.filename); return next; });
        await loadCategories();
      } else {
        setError(result.error || 'Delete failed');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    }
  };

  const handleBulkDelete = async () => {
    const deletable = allAssets.filter(a => selectedAssets.has(a.filename) && !isSyncedAsset(a));
    if (deletable.length === 0) {
      setError('No deletable images selected. Synced assets cannot be deleted.');
      return;
    }
    if (!window.confirm(`Delete ${deletable.length} selected image${deletable.length > 1 ? 's' : ''}?`)) return;

    setError(null);
    let deleted = 0;
    for (const asset of deletable) {
      try {
        const path = `${asset.subcategory}/${asset.filename}`;
        const result = await imageAssetsService.deleteCustomImage(path);
        if (result.success) deleted++;
      } catch {
        // continue with remaining
      }
    }
    setSuccessMessage(`Deleted ${deleted} of ${deletable.length} images`);
    setSelectedAssets(new Set());
    await loadCategories();
  };

  const toggleAssetSelection = (filename: string) => {
    setSelectedAssets(prev => {
      const next = new Set(prev);
      if (next.has(filename)) next.delete(filename);
      else next.add(filename);
      return next;
    });
  };

  const selectAll = () => {
    if (selectedAssets.size === allAssets.length) {
      setSelectedAssets(new Set());
    } else {
      setSelectedAssets(new Set(allAssets.map(a => a.filename)));
    }
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'Unknown';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Messages */}
      {error && (
        <div className="mx-6 mt-4 bg-rose-50 border border-rose-200 rounded-lg p-4 flex items-center gap-3">
          <svg className="w-5 h-5 text-rose-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          <span className="text-rose-800 text-sm flex-1">{error}</span>
          <button onClick={() => setError(null)} className="text-rose-600 hover:text-rose-800" aria-label="Dismiss error">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {successMessage && (
        <div className="mx-6 mt-4 bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-center gap-3">
          <Check className="w-5 h-5 text-emerald-500 flex-shrink-0" />
          <span className="text-emerald-800 text-sm flex-1">{successMessage}</span>
        </div>
      )}

      <div className="flex-1 flex overflow-hidden">
        {/* Secondary Sidebar - Categories (Duralux style) */}
        <div className="w-[260px] flex-shrink-0 bg-white border-r border-slate-200 flex flex-col">
          <div className="sticky top-0">
            <div className="h-14 px-6 flex items-center border-b border-slate-200">
              <h4 className="text-base font-bold text-slate-900">Categories</h4>
            </div>
            <div className="py-3 px-3">
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-600"></div>
                </div>
              ) : (
                <nav className="space-y-1">
                  {categories.map(cat => (
                    <button
                      key={cat.name}
                      onClick={() => { setSelectedCategory(cat.name); setSelectedAssets(new Set()); }}
                      className={cn(
                        'w-full flex items-center justify-between px-3 py-2.5 rounded-md transition-all text-[13px] font-semibold',
                        selectedCategory === cat.name
                          ? 'bg-orange-50 text-orange-700'
                          : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                      )}
                    >
                      <span>{cat.displayName}</span>
                      <span className={cn(
                        'text-xs tabular-nums',
                        selectedCategory === cat.name ? 'text-orange-500' : 'text-slate-400'
                      )}>
                        {cat.subcategories.reduce((sum, s) => sum + s.assets.length, 0)}
                      </span>
                    </button>
                  ))}
                </nav>
              )}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {/* Header with Upload */}
          <div className="p-4 border-b border-slate-200 bg-slate-50/50">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="font-semibold text-slate-900">{currentCategory?.displayName || 'Images'}</h3>
                <p className="text-xs text-slate-500 mt-1">
                  {currentCategory ? `${allAssets.length} images` : 'Select a category'}
                </p>
              </div>
              <div className="flex items-center gap-3">
                {selectedAssets.size > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-600">{selectedAssets.size} selected</span>
                    <Button variant="destructive" size="sm" onClick={handleBulkDelete}>
                      <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete Selected
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setSelectedAssets(new Set())}>
                      Clear
                    </Button>
                  </div>
                )}
                {allAssets.length > 0 && (
                  <Button variant="ghost" size="sm" onClick={selectAll}>
                    {selectedAssets.size === allAssets.length ? 'Deselect All' : 'Select All'}
                  </Button>
                )}
                <Button variant="ghost" size="icon-sm" onClick={loadCategories} title="Refresh">
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Drag & Drop Upload Zone */}
            <div
              className={cn(
                'relative border-2 border-dashed rounded-lg p-6 text-center transition-colors',
                dragActive
                  ? 'border-orange-500 bg-orange-50'
                  : 'border-slate-300 hover:border-slate-400 bg-white'
              )}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileInput}
                className="hidden"
                aria-label="Select image to upload"
              />
              <div className="flex items-center justify-center gap-4">
                {isUploading ? (
                  <div className="flex items-center gap-3">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-600"></div>
                    <span className="text-sm text-slate-600">Uploading...</span>
                  </div>
                ) : (
                  <>
                    <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                      <Upload className="w-5 h-5 text-slate-500" />
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-medium text-slate-700">
                        Drag and drop images here, or{' '}
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          className="text-orange-600 hover:text-orange-700 font-semibold"
                        >
                          browse files
                        </button>
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5">PNG, JPG, GIF up to 10MB</p>
                    </div>
                    <div className="ml-auto flex items-center gap-2">
                      <select
                        value={`${uploadCategory}/${uploadSubcategory}`}
                        onChange={(e) => { const [cat, sub] = e.target.value.split('/'); setUploadCategory(cat); setUploadSubcategory(sub); }}
                        className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 bg-white"
                        aria-label="Upload destination"
                      >
                        <option value="custom/uploads">Custom / Uploads</option>
                        <option value="custom/software">Custom / Software</option>
                        <option value="custom/hardware">Custom / Hardware</option>
                        <option value="custom/consulting">Custom / Consulting</option>
                      </select>
                      <Button size="sm" onClick={() => fileInputRef.current?.click()} disabled={isUploading}>
                        <Upload className="w-3.5 h-3.5 mr-1" /> Upload
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Image Grid */}
          <div className="flex-1 overflow-y-auto p-4">
            {!currentCategory ? (
              <div className="flex items-center justify-center h-full text-slate-500">
                <div className="text-center">
                  <ImageIcon className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p>Select a category to view images</p>
                </div>
              </div>
            ) : allAssets.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-slate-500">
                <ImageIcon className="w-12 h-12 text-slate-300 mb-3" />
                <p className="font-medium">No images in this category</p>
                <p className="text-sm text-slate-400 mt-1">Upload an image to get started</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {allAssets.map((asset, idx) => {
                  const synced = isSyncedAsset(asset);
                  const isSelected = selectedAssets.has(asset.filename);
                  return (
                    <div
                      key={`${asset.filename}-${idx}`}
                      className={cn(
                        'group relative bg-white rounded-lg border overflow-hidden transition-all duration-200',
                        isSelected
                          ? 'border-orange-400 ring-2 ring-orange-200 shadow-md'
                          : synced
                            ? 'border-slate-200 hover:border-slate-300 hover:shadow-md'
                            : 'border-blue-200 hover:border-blue-300 hover:shadow-md'
                      )}
                    >
                      {/* Selection checkbox */}
                      <div className="absolute top-2 left-2 z-10">
                        <button
                          onClick={() => toggleAssetSelection(asset.filename)}
                          className={cn(
                            'w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all',
                            isSelected
                              ? 'bg-orange-600 border-orange-600 text-white'
                              : 'bg-white/80 border-slate-300 text-transparent opacity-0 group-hover:opacity-100'
                          )}
                          aria-label={`Select ${asset.displayName}`}
                        >
                          <Check className="w-3.5 h-3.5" />
                        </button>
                      </div>

                      {/* Synced badge */}
                      {synced && (
                        <div className="absolute top-2 right-2 z-10">
                          <Badge variant="secondary" size="sm">Synced</Badge>
                        </div>
                      )}

                      {/* Image preview */}
                      <div className="aspect-square p-3 flex items-center justify-center bg-slate-50">
                        <img
                          src={asset.thumbnailUrl || asset.url}
                          alt={asset.displayName}
                          className="max-w-full max-h-full object-contain"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCA2NCA2NCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjY0IiBoZWlnaHQ9IjY0IiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0yMCAyMEg0NEwyNiA0MEwyMCAyMFoiIGZpbGw9IiM5Q0EzQUYiLz4KPC9zdmc+';
                          }}
                        />
                      </div>

                      {/* Hover overlay with actions */}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setPreviewAsset(asset)}
                            className="w-9 h-9 rounded-full bg-white/90 hover:bg-white flex items-center justify-center text-slate-700 shadow-sm transition-colors"
                            title="Preview"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          {!synced && (
                            <button
                              onClick={() => handleDelete(asset)}
                              className="w-9 h-9 rounded-full bg-white/90 hover:bg-white flex items-center justify-center text-rose-600 shadow-sm transition-colors"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Info footer */}
                      <div className="px-3 py-2.5 border-t border-slate-100">
                        <p className="text-xs font-medium text-slate-700 truncate" title={asset.displayName}>
                          {asset.displayName}
                        </p>
                        <div className="flex items-center justify-between mt-1">
                          <p className="text-[10px] text-slate-400 truncate" title={asset.filename}>
                            {asset.filename}
                          </p>
                          <p className="text-[10px] text-slate-400 flex-shrink-0 ml-2">
                            {formatFileSize((asset as any).size)}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Image Preview Modal */}
      {previewAsset && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={() => setPreviewAsset(null)}>
          <div className="absolute inset-0 bg-black/70" />
          <div className="relative max-w-3xl max-h-[80vh] p-2" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setPreviewAsset(null)}
              className="absolute -top-2 -right-2 z-10 w-8 h-8 rounded-full bg-white shadow-lg flex items-center justify-center text-slate-600 hover:text-slate-900"
              aria-label="Close preview"
            >
              <X className="w-4 h-4" />
            </button>
            <img
              src={previewAsset.url}
              alt={previewAsset.displayName}
              className="max-w-full max-h-[75vh] object-contain rounded-lg shadow-2xl"
            />
            <div className="mt-3 text-center">
              <p className="text-white font-medium">{previewAsset.displayName}</p>
              <p className="text-white/60 text-sm">{previewAsset.filename}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
